import React, {useState} from 'react';
import {Box, Typography} from '@mui/material';
import ArrButton from '../../common/arr-button/ArrButton';
import {ArrDrawer} from '../../common/arr-drawer/ArrDrawer';
import './UsersNotYetLoggedIn.scss';
import {t} from 'i18next';
import ArrDataGrid from '../../common/arr-data-grid/ArrDataGrid';
import {ReactComponent as InfoIcon} from '../../../../src/assets/images/info-icon.svg';
import {
  GridColDef,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid';
import ArrTooltip from '../../common/arr-tooltip/ArrTooltip';
import {GRID_SORT_ORDER, PAGE_SIZE, ToastType} from '../../../shared/constants';
import {
  useNotYetLoggedInUsers,
  useResendLink,
} from '../../../hooks/users/useUsers';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
interface UsersNotYetLoggedInProps {
  showPanel: boolean;
  setShowPanel: React.Dispatch<React.SetStateAction<boolean>>;
}
interface ResendRow {
  userProjectLinkID: string;
  projectLinkGUID: string;
  firstName: string;
  emailID: string;
  emaillinkUrl: string;
  domainLabel: string;
  projectLabel: string;
}

const INITIAL_SORT_MODEL: GridSortModel = [{field: 'firstName', sort: 'asc'}];
const INITIAL_PAGE = 1;

const UsersNotYetLoggedIn: React.FC<UsersNotYetLoggedInProps> = ({
  showPanel,
  setShowPanel,
}) => {
  const [currentPage, setCurrentPage] = useState(INITIAL_PAGE);
  const [showToast, setShowToast] = useState(false);
  const [sortModel, setSortModel] = useState<GridSortModel>(INITIAL_SORT_MODEL);
  const [toastStatuses, setToastStatuses] = useState<ToastType[]>([]);
  const userRoleId = useSelector(
    (state: RootState) => state.authenticatedUser.user?.roleId
  );

  const resetState = () => {
    setCurrentPage(INITIAL_PAGE);
    setSortModel(INITIAL_SORT_MODEL);
    setShowToast(false);
    setToastStatuses([]);
  };

  const onClose = () => {
    setShowPanel(false);
    resetState();
  };

  const handleOnPageChange = (page: number) => setCurrentPage(page);

  const onResend = (row: ResendRow) => {
    const {
      userProjectLinkID,
      projectLinkGUID,
      firstName,
      emailID,
      emaillinkUrl,
      domainLabel,
      projectLabel,
    } = row;
    resendLink(
      {
        userProjectLinkId: userProjectLinkID,
        firstName: firstName,
        emailId: emailID,
        emailLinkUrl: emaillinkUrl,
        domainLabel: domainLabel,
        projectLabel: projectLabel,
        projectLinkGUID: projectLinkGUID,
      },
      {
        onSuccess: () => {
          setShowToast(true);

          setToastStatuses((prev) => [...prev, ToastType.Success]);
        },
        onError: () => {
          setShowToast(true);
          setToastStatuses((prev) => [...prev, ToastType.Error]);
        },
      }
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'firstName',
      headerName: t('userManagement.notYetLoggedInUsers.firstName'),
      flex: 1,
    },
    {
      field: 'lastName',
      headerName: t('userManagement.notYetLoggedInUsers.lastName'),
      flex: 1,
    },
    {
      field: 'emailID',
      headerName: t('userManagement.notYetLoggedInUsers.emailId'),
      flex: 2,
    },
    {
      field: 'actions',
      headerName: '',
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: ({row}: GridRenderCellParams) => (
        <ArrButton
          variant="secondary"
          size="xs"
          onClick={() => onResend(row)}
          aria-label={`resend-${row.emailID}`}
          dataTestId={`resend-btn-${row.id}`}
        >
          {t('common.resend')}
        </ArrButton>
      ),
    },
  ];

  const NoRowsOverlay = () => (
    <Box className="no-data-found" data-testid="no-data-found">
      <Typography
        className="typo-text body3_size"
        data-testid={`users-not-yet-logged-in-no-data-found`}
      >
        {t('userManagement.notYetLoggedInUsers.noDataFound')}
      </Typography>
    </Box>
  );

  const payload = {
    pageNumber: currentPage,
    displayLength: PAGE_SIZE,
    filterRoleId: userRoleId || 0,
    sortingdirection: sortModel[0]?.sort || 'asc',
    sortingColumn: sortModel[0]?.field,
  };

  const {data: usersInfo, isLoading: isNotLoggedInUsersDataLoading} =
    useNotYetLoggedInUsers(payload);
  const {mutate: resendLink, error: resendLinkError} = useResendLink();
  const drawerContents = (
    <Box className="user-not-logged-in__panel">
      <Box className="user-not-logged-in__header">
        <Typography
          className="typo-text headline3_size text-color"
          data-testid={`users-not-yet-logged-in-heading`}
        >
          {t('userManagement.notYetLoggedInUsers.heading')}
        </Typography>
        <ArrTooltip
          content={t('userManagement.notYetLoggedInUsers.infoContent')}
          trigger="click"
          placement="right"
          className="info-tooltip"
        >
          <InfoIcon className="info-icon" data-testid="info-tool-tip" />
        </ArrTooltip>
      </Box>

      <Box
        className="user-not-logged-in__content"
        data-testid="not-logged-in-user-table"
      >
        <ArrDataGrid
          rows={usersInfo?.data ?? []}
          primaryKey="emailID"
          entityType={t('common.entries')}
          rowCount={usersInfo?.totalRecords}
          columns={columns.map((col, index) => ({
            ...col,
            headerClassName:
              index === 0 ? 'first-column-header' : col.headerClassName,
            cellClassName:
              index === 0 ? 'first-column-cell' : col.cellClassName,
          }))}
          sortingOrder={GRID_SORT_ORDER}
          noRowsOverlay={NoRowsOverlay}
          className="user-data-grid"
          onPageChange={handleOnPageChange}
          onSortModelChange={setSortModel}
          currentPage={currentPage}
          itemsPerPage={PAGE_SIZE}
          sortModel={sortModel}
          sortingMode="server"
          loading={isNotLoggedInUsersDataLoading}
        />
      </Box>
    </Box>
  );
  const handleToastClose = () => {
    setShowToast(false);
    setToastStatuses([]);
  };
  return (
    <Box>
      <ArrDrawer
        isOpen={showPanel}
        onClose={onClose}
        ariaLabel="drawer content"
        size="large"
        side="right"
        showButton
      >
        {drawerContents}
      </ArrDrawer>

      {showToast && (
        <ArrToastNotification
          message={
            toastStatuses[toastStatuses.length - 1] === ToastType.Success
              ? t('userManagement.notYetLoggedInUsers.resendSuccessMessage')
              : (resendLinkError?.message ?? '')
          }
          onClose={handleToastClose}
          status={toastStatuses}
        />
      )}
    </Box>
  );
};

export default UsersNotYetLoggedIn;
